import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  firstValueFrom,
  takeUntil,
} from 'rxjs';
import { EveCall } from '../models/eve-call';
import { LogModule, LogService } from './log.service';
import { SipService } from './sip.service';
import { SessionDescriptionHandler } from 'sip.js/lib/platform/web';
import { SessionState } from 'sip.js';
import { error } from 'console';

export interface ConferenceRoom {
  id: string;
  name: string;
  participants: EveCall[];
  createdAt: Date;
  isOnHold: boolean; // Only one conference can be active at a time
}

@Injectable({
  providedIn: 'root',
})
export class ConferenceService implements OnDestroy {
  // Track all conferences
  private conferencesSubject = new BehaviorSubject<ConferenceRoom[]>([]);
  public conferences$ = this.conferencesSubject.asObservable();

  private organizerMuteStates = new Map<string, boolean>();

  // ✅ SIMPLIFIED: Keep only the simple audio setup
  // Audio graph
  private ctx = new (window.AudioContext ||
    (window as any).webkitAudioContext)();
  private monitorDest = this.ctx.createMediaStreamDestination(); // local speakers
  private masterGain = this.ctx.createGain(); // monitor bus (you hear)
  private mixOut = this.ctx.createMediaStreamDestination(); // send bus (remotes hear)
  private sendBus = this.ctx.createGain(); // summed outbound mix
  private outputEl?: HTMLAudioElement;

  // Mic
  private micSource?: MediaStreamAudioSourceNode;
  private micGain = this.ctx.createGain();

  private destroySubject = new Subject<void>();
  // Per-participant nodes
  private nodes = new Map<
    string,
    {
      source: MediaStreamAudioSourceNode;
      monitorGain: GainNode; // controls what YOU hear
      sendGain: GainNode; // controls what OTHERS hear
    }
  >();

  // ✅ FIXED: Store original microphone STREAM instead of track
  private originalMicrophoneStream: MediaStream | null = null;

  private originalOrganizerSpeakerDestination: MediaStreamAudioDestinationNode | null =
    null;

  constructor(private sipService: SipService) {
    // local monitor: masterGain -> monitorDest
    this.masterGain.connect(this.monitorDest);

    // outbound mix: sendBus -> mixOut
    this.sendBus.connect(this.mixOut);

    this.sipService.selectedSpeakerId$
      .pipe(takeUntil(this.destroySubject))
      .subscribe(async (deviceId) => {
        await this.applySink(this.outputEl, deviceId);
        const el = document.getElementById(
          'organizer-conference-audio'
        ) as HTMLAudioElement | null;
        await this.applySink(el, deviceId);
      });

    this.sipService.callTerminated$
      .pipe(takeUntil(this.destroySubject))
      .subscribe((event) => {
        this.handleTerminatedParticipant(event);
      });
  }
  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  private async applySink(
    el: HTMLAudioElement | null | undefined,
    deviceId: string | null
  ) {
    if (!el || !deviceId) return;
    const canSet = 'setSinkId' in HTMLMediaElement.prototype;
    if (!canSet) return; // Safari/iOS don’t support it
    try {
      await (el as any).setSinkId(deviceId);
    } catch (err) {
      console.warn('[Conference] setSinkId failed:', err);
    }
  }

  // ✅ Simple output binding for organizer
  bindOutput(el: HTMLAudioElement) {
    this.outputEl = el;
    this.outputEl.srcObject = this.monitorDest.stream;
    this.outputEl.muted = false;
    this.outputEl.play?.().catch(() => {});
    // NEW: apply selected speaker
    this.applySink(this.outputEl, this.sipService.getSelectedSpeaker());
  }

  async addParticipant(call: EveCall): Promise<void> {
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    // ✅ NEW: Store original mute state and temporarily unmute for audio setup
    const wasOriginallyMuted = call.isMuted;
    if (wasOriginallyMuted) {
      // Temporarily enable tracks for proper audio node creation
      const sender = this.getAudioSender(call);
      const receiver = this.getAudioReceiver(call);
      if (sender?.track) sender.track.enabled = true;
      if (receiver?.track) receiver.track.enabled = true;

      console.log(
        `🔧 Temporarily enabled tracks for ${call.remoteUser} audio setup`
      );
    }
    await this.ensureEstablished(call);

    // --- Make the leg active first (unhold/force direction), then WAIT for frames
    const earlyUnhold = call.isOnHold;
    if (earlyUnhold) {
      await call.unholdCall();
    }

    // build from the *current* receiver track AFTER unhold/reneg
    const remoteStream = this.getCurrentRxMediaStream(call);
    if (!remoteStream)
      throw new Error(`No remote stream for ${call.remoteUser}`);

    // --- Now wire the graph (one source → two gains)
    const source = this.ctx.createMediaStreamSource(remoteStream);
    const monitorGain = this.ctx.createGain();
    const sendGain = this.ctx.createGain();
    const initial = call.isMuted ? 0 : 1;
    monitorGain.gain.value = initial;
    sendGain.gain.value = initial;

    source.connect(monitorGain).connect(this.masterGain); // you hear them
    // source.connect(sendGain).connect(this.sendBus); // others hear them

    this.nodes.set(this.callKey(call), { source, monitorGain, sendGain });

    // ✅ FIX: Rebuild individual mixes for ALL participants (not just the new one)
    await this.rebuildAllIndividualMixes();

    console.log(`✅ Added ${call.remoteUser} (mute:${call.isMuted})`);
  }

  // ✅ NEW: Rebuild individual mixes for ALL current participants
  private async rebuildAllIndividualMixes(): Promise<void> {
    // Get all participants from this.nodes
    const allParticipantKeys = Array.from(this.nodes.keys());

    for (const participantKey of allParticipantKeys) {
      // Find the call object for this key
      const call = this.findCallByKey(participantKey);
      if (call) {
        await this.createIndividualMixForCall(call);
      }
    }
  }

  // ✅ NEW: Helper to find call by key
  private findCallByKey(callKey: string): EveCall | null {
    const conferences = this.conferencesSubject.value;
    for (const conf of conferences) {
      const call = conf.participants.find((p) => this.callKey(p) === callKey);
      if (call) return call;
    }
    return null;
  }

  private async createIndividualMixForCall(call: EveCall): Promise<void> {
    const individualMix = this.ctx.createMediaStreamDestination();
    const mixGain = this.ctx.createGain();
    mixGain.gain.value = 1.0;
    mixGain.connect(individualMix);

    // ✅ FIX: Add organizer's microphone to this participant's mix (only if not muted)
    const activeConference = this.getActiveConference();
    const isOrganizerMuted = activeConference
      ? this.isOrganizerMuted(activeConference)
      : false;

    if (this.micSource && !isOrganizerMuted) {
      const micGain = this.ctx.createGain();
      micGain.gain.value = 1.0;
      this.micSource.connect(micGain);
      micGain.connect(mixGain);
    }

    // Add OTHER participants' audio (exclude this participant's own voice)
    const thisCallKey = this.callKey(call);
    this.nodes.forEach((node, nodeKey) => {
      if (nodeKey === thisCallKey) return; // ✅ Skip own voice

      // 🔽 NEW: skip if that participant is muted
      const otherCall = this.findCallByKey(nodeKey);
      if (!otherCall || otherCall.isMuted) return;

      const otherGain = this.ctx.createGain();
      otherGain.gain.value = 1.0;
      node.source.connect(otherGain);
      otherGain.connect(mixGain);
    });

    // Send this individual mix to the participant
    await this.attachIndividualMixToCall(call, individualMix);
  }

  private async attachIndividualMixToCall(
    call: EveCall,
    mixDestination: MediaStreamAudioDestinationNode
  ): Promise<void> {
    const sdh: any = call.session?.sessionDescriptionHandler;
    const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
    if (!pc) return;

    const mixTrack = mixDestination.stream.getAudioTracks()[0];
    if (!mixTrack) return;

    let sender = pc
      .getSenders()
      .find((s) => s.track && s.track.kind === 'audio');
    if (sender) {
      await sender.replaceTrack(mixTrack);
    } else {
      pc.addTrack(mixTrack, mixDestination.stream);
    }
  }

  private async ensureEstablished(call: EveCall, timeoutMs = 6000) {
    const sess: any = call.session;
    if (!sess) return;
    const done = new Promise<void>((resolve) => {
      if (sess.state === 'Established') return resolve();
      const off = sess.stateChange.addListener((s: any) => {
        if (s === 'Established') {
          off();
          resolve();
        }
      });
    });
    await Promise.race([done, this.delay(timeoutMs)]);
  }

  // ✅ Simple conference creation
  async createConference(activeCall: EveCall): Promise<ConferenceRoom> {
    const conference: ConferenceRoom = {
      id: this.generateConferenceId(),
      name: `Conference ${Math.random().toString(36).substr(2, 4)}`,
      participants: [activeCall],
      createdAt: new Date(),
      isOnHold: false,
    };

    activeCall.isInConference = true;
    activeCall.conferenceId = conference.id;

    // ✅ CRITICAL: Store original organizer speaker destination BEFORE creating conference mix
    this.originalOrganizerSpeakerDestination = this.monitorDest;
    console.log(
      '🔊 Stored original organizer speaker destination for conference'
    );

    // ✅ SIMPLE: Set up microphone and audio using ONLY the simple system
    await this.setupMicrophone();
    await this.addParticipant(activeCall);

    const conferences = this.conferencesSubject.value;
    this.conferencesSubject.next([...conferences, conference]);

    if (activeCall.isMuted) await this.muteParticipantInConference(activeCall);
    console.log(
      `✅ Conference created with simple system: ${activeCall.remoteUser}`
    );
    return conference;
  }

  // ✅ Simple microphone setup
  // ✅ ENHANCED: Better setupMicrophone method that preserves original track
  private async setupMicrophone(): Promise<void> {
    if (this.micSource) return;

    // ✅ CRITICAL: Get the CURRENT microphone stream from SipService BEFORE creating conference mix
    const sipMicStream = this.sipService.getActiveStream();
    if (sipMicStream && sipMicStream.getAudioTracks().length > 0) {
      this.originalMicrophoneStream = sipMicStream;
      console.log('🎤 Stored original microphone STREAM for conference');
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });

    this.micSource = this.ctx.createMediaStreamSource(stream);
    this.micGain.gain.value = 1;
    this.micSource.connect(this.micGain);
    this.micGain.connect(this.sendBus); // others hear you
  }

  private getCurrentRxMediaStream(call: EveCall): MediaStream | null {
    const pc = (call.session as any)?.sessionDescriptionHandler
      ?.peerConnection as RTCPeerConnection | undefined;
    if (!pc) return null;
    const rx = pc.getReceivers().find((r) => r.track?.kind === 'audio')?.track;
    if (!rx) return null;
    return new MediaStream([rx]); // <- stays valid even if sdh.remoteMediaStream object changes
  }

  private key = new WeakMap<EveCall, string>();
  // ...existing helper methods...
  private callKey(call: EveCall): string {
    if (this.key.has(call)) {
      return this.key.get(call)!;
    } else {
      const id = call.id ?? crypto.randomUUID();
      this.key.set(call, id);
      return id;
    }
  }

  /**
   * MAIN MERGE LOGIC: Handles both scenarios based on the calling context
   */
  async mergeCallIntoConference(call: EveCall): Promise<ConferenceRoom> {
    const isUnholdCall = !call.isOnHold && !call.isInConference;
    const unholdConference = this.getActiveConference();

    if (isUnholdCall) {
      throw new Error(
        'Cannot create new active conference or join existing one while the call is unholding.'
      );
    }

    const unholdCall = this.sipService.getActiveCalls().find((c) => {
      return !c.isOnHold && !c.isInConference;
    });

    let conference: ConferenceRoom | null = null;
    if (unholdCall && !isUnholdCall && !unholdConference) {
      conference = await this.createConference(unholdCall); // activeCall is already in participants
    }

    conference = conference || unholdConference;

    if (!conference) {
      throw new Error('No active conference available to add the call to.');
    }

    // ✅ CRITICAL: Check if the call is muted BEFORE adding to conference
    const wasCallMuted = call.isMuted;

    // ✅ STEP 1: If call is muted, unmute it temporarily for proper audio setup
    if (wasCallMuted) {
      console.log(
        `📞 Call ${call.remoteUser} is muted - temporarily unmuting for conference setup`
      );

      // Unmute the individual call first
      await call.unmute();

      // Small delay to ensure audio tracks are properly enabled
      await this.delay(100);
    }

    // ✅ FIX: Manually add the second call to participants BEFORE audio setup
    call.isInConference = true;
    call.conferenceId = conference.id;
    conference.participants.push(call); // ✅ Add to participants array
    // ✅ Update the conoference in the subject BEFORE audio setup
    this.updateConferenceInList(conference);

    await this.addParticipant(call); // Now set up audio
    await this.setupOrganizerAudioFromSimpleSystem();

    // ✅ STEP 3: If the call was originally muted, mute it again in conference context
    if (wasCallMuted) {
      console.log(`🔇 Re-muting ${call.remoteUser} in conference context`);
      await this.muteParticipantInConference(call);
    }
    return conference;
  }

  private async setupOrganizerAudioFromSimpleSystem(): Promise<void> {
    // Create or get the organizer audio element
    let organizerAudioEl = document.getElementById(
      'organizer-conference-audio'
    ) as HTMLAudioElement;

    if (!organizerAudioEl) {
      organizerAudioEl = document.createElement('audio');
      organizerAudioEl.id = 'organizer-conference-audio';
      organizerAudioEl.autoplay = true;
      organizerAudioEl.muted = false;
      organizerAudioEl.volume = 1.0;
      organizerAudioEl.style.display = 'none';
      document.body.appendChild(organizerAudioEl);
    }

    // ✅ Connect to the simple system's monitor destination
    organizerAudioEl.srcObject = this.monitorDest.stream; // This is from your simple system

    try {
      await organizerAudioEl.play();

      // Apply selected speaker
      const selectedSpeaker = this.sipService.getSelectedSpeaker();
      if (selectedSpeaker && 'setSinkId' in HTMLMediaElement.prototype) {
        await (organizerAudioEl as any).setSinkId(selectedSpeaker);
      }
    } catch (error) {
      console.warn('Could not play organizer audio:', error);
    }
  }

  /** Deactivate a conference: stop mixing, hold all calls, mute organizer speaker. */
  /** Deactivate a conference: stop mixing, hold all calls, mute organizer speaker. */
  async deactivateConference(conference: ConferenceRoom): Promise<void> {
    if (!conference) return;
    if (conference.isOnHold) {
      console.log(`ℹ️ Conference ${conference.id} already on hold`);
      return;
    }

    console.log(`⏸️ Deactivating conference ${conference.id}`);

    // 1) Disconnect all participant nodes
    for (const call of conference.participants) {
      const key = this.callKey(call);
      const n = this.nodes.get(key);
      if (n) {
        try {
          n.source.disconnect();
        } catch {}
        try {
          n.monitorGain.disconnect();
        } catch {}
        try {
          n.sendGain.disconnect();
        } catch {}
        this.nodes.delete(key);
      }
    }

    // ✅ Step 2: Clean up organizer audio PROPERLY
    const organizerAudioEl = document.getElementById(
      'organizer-conference-audio'
    ) as HTMLAudioElement;
    if (organizerAudioEl) {
      organizerAudioEl.pause();
      organizerAudioEl.srcObject = null;
      console.log('🔊 Paused and cleared organizer conference audio element');
    }

    // ✅ Step 3: Disconnect microphone from mixing buses (but keep micSource reference)
    if (this.micSource) {
      try {
        this.micSource.disconnect();
      } catch {}
      try {
        this.micGain.disconnect();
      } catch {}
      console.log('🎤 Disconnected microphone from conference mixing');
    }

    // ✅ Step 4: Disconnect masterGain from current destination (but keep reference)
    try {
      this.masterGain.disconnect();
      console.log('🔊 Disconnected masterGain from monitor destination');
    } catch {}

    // ✅ CRITICAL FIX: Convert ALL participants back to 1-to-1 mode FIRST (before holding)
    for (const call of conference.participants) {
      if (call.session?.state !== SessionState.Terminated) {
        console.log(
          `🔄 Converting ${call.remoteUser} back to 1-to-1 mode during deactivation`
        );
        const conferenceId = call.conferenceId;
        await this.convertToOneToOne(call);
        call.conferenceId = conferenceId;
        call.isInConference = true;
        await this.delay(100);
      }

      // Clear conference flags (convertToOneToOne should have done this, but ensure it's done)
      // call.isInConference = false;
      // call.conferenceId = null;
    }

    // 5) Hold every call AFTER converting to 1-to-1
    for (const call of conference.participants) {
      if (call.session?.state !== SessionState.Terminated && !call.isOnHold) {
        console.log(`📞 Holding ${call.remoteUser} after 1-to-1 conversion`);
        await call.holdCall();
        await this.delay(80);
      }
    }

    // 6) Mark held & notify
    conference.isOnHold = true;
    this.updateConferenceInList(conference);
    console.log(`✅ Conference ${conference.id} deactivated`);
  }
  // ✅ ENHANCED: Better removeFromConference method
  async removeFromConference(call: EveCall): Promise<void> {
    const conferences = this.conferencesSubject.value;
    const conference = conferences.find((c) =>
      c.participants.some((p) => this.callKey(p) === this.callKey(call))
    );

    if (!conference) return;

    try {
      console.log(
        `🔄 Removing ${call.remoteUser} from conference ${conference.id}`
      );

      const callKey = this.callKey(call);
      const isCallTerminated = call.session?.state === SessionState.Terminated;

      // ✅ STEP 1: Check if conference should end (less than 2 participants)
      if (conference.participants.length <= 2) {
        console.log(
          `📞 Conference ${conference.id} has ${conference.participants.length} participants - ending conference`
        );
        await this.endConference(conference);
        return; // Don't continue processing after ending conference
      }

      // ✅ STEP 2: Complete audio cleanup for the removed participant
      if (!conference.isOnHold && !isCallTerminated) {
        await this.completeParticipantAudioCleanup(call);
      } else if (isCallTerminated) {
        // For terminated calls, just clean up the audio nodes
        const node = this.nodes.get(callKey);
        if (node) {
          try {
            node.source.disconnect();
            node.monitorGain.disconnect();
            node.sendGain.disconnect();
          } catch (err) {
            console.warn(
              `⚠️ Audio node cleanup warning for terminated call:`,
              err
            );
          }
          this.nodes.delete(callKey);
        }
      }

      // ✅ STEP 3: Remove from participants list FIRST
      conference.participants = conference.participants.filter(
        (c) => this.callKey(c) !== callKey
      );

      // ✅ STEP 4: Clear conference flags from the call
      call.isInConference = false;
      call.conferenceId = null;

      // ✅ STEP 5: Convert the removed call to 1-to-1 mode (if not terminated)
      if (!isCallTerminated) {
        await this.convertToOneToOne(call);

        // Hold the call after conversion
        if (!call.isOnHold) {
          await call.holdCall();
          await this.delay(100);
        }
      }

      // ✅ STEP 6: Rebuild audio mixing for remaining participants (if conference is active)
      if (!conference.isOnHold && conference.participants.length >= 2) {
        console.log(
          `🔄 Rebuilding audio for remaining ${conference.participants.length} participants`
        );
        await this.rebuildAllIndividualMixes();
      }

      // ✅ STEP 7: Update conference state
      this.updateConferenceInList(conference);
      if (!conference.isOnHold) {
        this.setActiveConference(conference);
      }

      console.log(
        `✅ Removed ${call.remoteUser} from conference ${conference.id}`
      );
    } catch (error: any) {
      console.error(`❌ Failed to remove participant from conference:`, error);
      throw new Error(
        `Failed to remove participant from conference: ${error.message}`
      );
    }
  }

  async endConference(conference: ConferenceRoom): Promise<void> {
    const conferences = this.conferencesSubject.value;
    if (!conference) return;

    try {
      console.log(`🔄 Ending conference ${conference.id}`);

      // ✅ Step 1: Clean up ALL audio nodes for conference participants
      for (const participant of conference.participants) {
        const callKey = this.callKey(participant);
        const node = this.nodes.get(callKey);
        if (node) {
          try {
            node.source.disconnect();
            node.monitorGain.disconnect();
            node.sendGain.disconnect();
          } catch (err) {
            console.warn(`⚠️ Audio cleanup warning:`, err);
          }
          this.nodes.delete(callKey);
        }
      }

      // ✅ Step 2: Clean up organizer microphone connections (but keep original stream reference)
      if (this.micSource) {
        try {
          this.micSource.disconnect();
          this.micGain.disconnect();
        } catch (err) {
          console.warn(`⚠️ Mic cleanup warning:`, err);
        }
        this.micSource = undefined;
        // ✅ NOTE: We DON'T clear originalMicrophoneStream here - we need it for restoration
      }

      // ✅ Step 3: Convert ALL participants back to 1-to-1 mode BEFORE holding
      for (const call of conference.participants) {
        if (call.session?.state !== SessionState.Terminated) {
          console.log(`🔄 Converting ${call.remoteUser} back to 1-to-1 mode`);
          await this.convertToOneToOne(call);
          await this.delay(100);
        }

        // Clear conference flags
        call.isInConference = false;
        call.conferenceId = null;
      }

      // ✅ Step 4: Hold all calls AFTER they're converted to 1-to-1
      for (const call of conference.participants) {
        if (!call.isOnHold && call.session?.state !== SessionState.Terminated) {
          console.log(`📞 Holding ${call.remoteUser} after 1-to-1 conversion`);
          await call.holdCall();
          await this.delay(50);
        }
      }

      // ✅ Step 5: Update SIP service active calls list
      const currentActiveCalls = this.sipService.getActiveCalls();
      const updatedCalls = currentActiveCalls.map((c) => {
        if (conference.participants.includes(c)) {
          c.isInConference = false;
          c.conferenceId = null;
        }
        return c;
      });
      this.sipService.setActiveCalls(updatedCalls);

      // ✅ Step 6: Clean up conference state
      this.organizerMuteStates.delete(conference.id);

      const updatedConferences = conferences.filter(
        (c) => c.id !== conference.id
      );
      this.conferencesSubject.next(updatedConferences);

      this.setActiveConference(null);

      // ✅ Step 7: Clean up organizer audio element
      const organizerAudioEl = document.getElementById(
        'organizer-conference-audio'
      ) as HTMLAudioElement;
      if (organizerAudioEl) {
        organizerAudioEl.pause();
        organizerAudioEl.srcObject = null;
        organizerAudioEl.remove();
      }

      // ✅ Step 8: Clear the original microphone stream reference (conference is fully ended)
      this.originalMicrophoneStream = null;
      this.originalOrganizerSpeakerDestination = null;

      console.log(`✅ Conference ${conference.id} ended successfully`);
    } catch (error: any) {
      console.error(`❌ Failed to end conference ${conference.id}:`, error);
      throw new Error(
        `Failed to end conference ${conference.name}: ${error.message}`
      );
    }
  }

  async endConferences(): Promise<void> {
    const conferences = this.conferencesSubject.value;
    for (const conference of conferences) {
      await this.endConference(conference);
      this.delay(200);
    }
  }

  // Helper methods for state management
  setActiveConference(conference: ConferenceRoom | null): void {
    if (conference) {
      conference.isOnHold = false;
    }
  }

  private updateConferenceInList(updatedConference: ConferenceRoom): void {
    const conferences = this.conferencesSubject.value;
    const updated = conferences.map((c) =>
      c.id === updatedConference.id ? updatedConference : c
    );
    this.conferencesSubject.next(updated);
  }

  private delay(ms: number) {
    return new Promise((res) => setTimeout(res, ms));
  }

  private generateConferenceId(): string {
    return 'conf_' + Math.random().toString(36).substr(2, 9);
  }

  // Public API methods (backward compatibility)
  getActiveConference(): ConferenceRoom | null {
    return this.getAllConferences().find((conf) => !conf.isOnHold) || null;
  }

  getAllConferences(): ConferenceRoom[] {
    return this.conferencesSubject.value;
  }

  clearActiveConference(): void {
    const active = this.getActiveConference();
    if (active) {
      this.endConference(active);
    }
  }

  hasActiveConference(): boolean {
    return !!this.getActiveConference();
  }

  /**
   * NEW: Activate a specific conference (makes it the active one)
   * This will hold all other calls/conferences and activate all calls in this conference
   */
  /** Activate a conference: unhold everyone, rebuild graph, reattach organizer speaker. */
  async activateConference(conference: ConferenceRoom): Promise<void> {
    if (!conference) return;
    if (!conference.isOnHold) {
      console.log(`ℹ️ Conference ${conference.id} already active`);
      return;
    }

    const currentActivatedConference = this.getActiveConference();

    if(currentActivatedConference){
      await this.deactivateConference(currentActivatedConference);
    }

    console.log(`▶️ Activating conference ${conference.id}`);

    // ✅ STEP 0: Ensure audio context is running
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    // ✅ STEP 1: Restore organizer speaker destination FIRST
    if (this.originalOrganizerSpeakerDestination) {
      console.log('🔊 Restoring original organizer speaker destination');
      // Reconnect masterGain to the original monitor destination
      try {
        this.masterGain.disconnect();
      } catch {}
      this.masterGain.connect(this.originalOrganizerSpeakerDestination);
      this.monitorDest = this.originalOrganizerSpeakerDestination;
    } else {
      // Fallback: reconnect to current monitorDest
      console.log('🔊 Reconnecting masterGain to monitorDest');
      try {
        this.masterGain.disconnect();
      } catch {}
      this.masterGain.connect(this.monitorDest);
    }

    // ✅ STEP 2: Restore microphone connections
    if (this.micSource) {
      console.log('🎤 Reconnecting microphone to conference mixing');
      try {
        // Ensure micGain is properly connected
        this.micGain.disconnect();
        this.micSource.connect(this.micGain);
        this.micGain.connect(this.sendBus); // others hear you
      } catch (err) {
        console.warn('⚠️ Error reconnecting microphone:', err);
      }
    } else {
      // Re-setup microphone if it's missing
      console.log('🎤 Re-setting up microphone for conference');
      await this.setupMicrophone();
    }

    // 3) Unhold every participant (resume media first)
    for (const call of conference.participants) {
      if (call.session?.state !== SessionState.Terminated && call.isOnHold) {
        await call.unholdCall();
        // give the remote track a moment to flow so MediaStreamSource won’t be silent
        await this.delay(120);
      }
    }

    // 4) Rebuild per-participant nodes & individual mixes
    for (const call of conference.participants) {
      try {
        await this.addParticipant(call); // creates source -> monitorGain -> masterGain; plus individual send mix
      } catch (e) {
        console.warn(`⚠️ Could not add ${call.remoteUser} to mix`, e);
      }
    }

    // 5) ✅ ENHANCED: Ensure the organizer monitor is properly restored
    await this.restoreOrganizerSpeaker();

    // ) Mark active & notify
    conference.isOnHold = false;
    this.updateConferenceInList(conference);

    const activityCalls = await firstValueFrom(this.sipService.activeCalls$);
    const unholdActivityCall = activityCalls.find(call => !call.isOnHold && !call.isInConference) || null;

    if(unholdActivityCall){
      await unholdActivityCall.holdCall();
      
      this.sipService.setActiveCalls(activityCalls);
    }
    console.log(`✅ Conference ${conference.id} activated`);
  }

  /**
   * NEW: Deactivate the current active conference
   * This will hold all calls in the conference and stop audio mixing
   */
  async deactivateCurrentConference(): Promise<void> {
    const activeConference = this.getActiveConference();
    if (!activeConference) {
      console.log('ℹ️ No active conference to deactivate');
      return;
    }

    console.log(`🔄 Deactivating current conference ${activeConference.id}`);
    await this.deactivateConference(activeConference);
    console.log(`✅ Current conference deactivated`);
  }

  /** Force the inbound remote audio track(s) for this call to produce silence (or audio). */
  private setRemoteTracksEnabled(call: EveCall, enabled: boolean): void {
    const stream = this.getCurrentRxMediaStream(call);
    if (!stream) return;
    for (const t of stream.getAudioTracks()) {
      t.enabled = enabled; // false => silence to ALL sinks (WebAudio + <audio> elements)
    }
  }

  /** Belt-and-braces: if any <audio>/<video> element is rendering this call’s stream, mute it. */
  private hardMuteDomSinksFor(call: EveCall, muted: boolean): void {
    const stream = this.getCurrentRxMediaStream(call);
    if (!stream) return;
    const ids = new Set(stream.getAudioTracks().map((t) => t.id));
    (
      document.querySelectorAll('audio,video') as NodeListOf<HTMLMediaElement>
    ).forEach((el) => {
      const so = (el as any).srcObject as MediaStream | null;
      if (!so) return;
      const has = so.getAudioTracks().some((t) => ids.has(t.id));
      if (has) {
        if (muted) {
          el.muted = true;
          el.volume = 0;
        } else {
          el.muted = false;
          el.volume = 1;
        }
      }
    });
  }

  async muteParticipantInConference(call: EveCall): Promise<void> {
    if (!call.isInConference) {
      throw new Error('Call is not in a conference');
    }

    // 1) Mark model state first
    call.isMuted = true;

    // 1) Track-level hard mute (kills ghost <audio> paths)
    this.setRemoteTracksEnabled(call, false);
    this.hardMuteDomSinksFor(call, true);

    // 2) Cut what YOU hear from this participant (monitor bus)
    const key = this.callKey(call);
    const node = this.nodes.get(key);
    if (node) {
      const t = this.ctx.currentTime;
      node.monitorGain.gain.cancelScheduledValues(t);
      node.monitorGain.gain.setTargetAtTime(0, t, 0.02);
    }

    // 3) Rebuild all peer mixes so OTHERS no longer receive this participant
    await this.rebuildAllIndividualMixes();
  }

  // Unmute a specific participant
  async unmuteParticipantInConference(call: EveCall): Promise<void> {
    if (!call.isInConference) {
      throw new Error('Call is not in a conference');
    }

    // 1) Mark model state first
    call.isMuted = false;

    // 1) Track-level hard mute (kills ghost <audio> paths)
    this.setRemoteTracksEnabled(call, true);
    this.hardMuteDomSinksFor(call, false);

    // 2) Restore what YOU hear from this participant
    const key = this.callKey(call);
    const node = this.nodes.get(key);
    if (node) {
      const t = this.ctx.currentTime;
      node.monitorGain.gain.cancelScheduledValues(t);
      node.monitorGain.gain.setTargetAtTime(1, t, 0.02);
    }

    // 3) Rebuild all peer mixes so this participant is included again
    await this.rebuildAllIndividualMixes();
  }

  async toggleParticipantMute(call: EveCall): Promise<void> {
    if (call.isInConference) {
      // Use conference-specific mute/unmute
      if (call.isMuted) {
        await this.unmuteParticipantInConference(call);
      } else {
        await this.muteParticipantInConference(call);
      }
    }
  }

  /**
   * Mute the organizer's microphone in the specified conference
   */
  async muteOrganizer(conference?: ConferenceRoom): Promise<void> {
    const targetConference = conference || this.getActiveConference();
    if (!targetConference) {
      throw new Error('No conference specified or active');
    }

    try {
      // ✅ Store mute state FIRST
      this.organizerMuteStates.set(targetConference.id, true);

      // ✅ SIMPLE FIX: Just mute the microphone gain
      if (this.micGain) {
        const t = this.ctx.currentTime;
        this.micGain.gain.cancelScheduledValues(t);
        this.micGain.gain.setTargetAtTime(0, t, 0.02); // Smooth fade to 0
      }

      // ✅ Rebuild individual mixes for all participants (so they don't hear organizer)
      await this.rebuildAllIndividualMixes();

      console.log(`✅ Organizer muted in conference ${targetConference.id}`);
    } catch (error: any) {
      // Rollback on error
      this.organizerMuteStates.set(targetConference.id, false);
      throw new Error(`Failed to mute organizer: ${error.message}`);
    }
  }

  /**
   * Unmute the organizer's microphone in the specified conference
   */
  async unmuteOrganizer(conference?: ConferenceRoom): Promise<void> {
    const targetConference = conference || this.getActiveConference();
    if (!targetConference) {
      throw new Error('No conference specified or active');
    }

    try {
      // ✅ Store unmute state FIRST
      this.organizerMuteStates.set(targetConference.id, false);

      // ✅ SIMPLE FIX: Just unmute the microphone gain
      if (this.micGain) {
        const t = this.ctx.currentTime;
        this.micGain.gain.cancelScheduledValues(t);
        this.micGain.gain.setTargetAtTime(1, t, 0.02); // Smooth fade to 1
      }

      // ✅ Rebuild individual mixes for all participants (so they can hear organizer)
      await this.rebuildAllIndividualMixes();

      console.log(`✅ Organizer unmuted in conference ${targetConference.id}`);
    } catch (error: any) {
      // Rollback on error
      this.organizerMuteStates.set(targetConference.id, true);
      throw new Error(`Failed to unmute organizer: ${error.message}`);
    }
  }

  /**
   * Toggle organizer mute state for the specified conference
   */
  async toggleOrganizerMute(conference?: ConferenceRoom): Promise<void> {
    const targetConference = conference || this.getActiveConference();
    if (!targetConference) {
      throw new Error('No conference specified or active');
    }

    const isCurrentlyMuted = this.isOrganizerMuted(targetConference);

    if (isCurrentlyMuted) {
      await this.unmuteOrganizer(targetConference);
    } else {
      await this.muteOrganizer(targetConference);
    }
  }

  /**
   * Check if organizer is muted in the specified conference
   */
  isOrganizerMuted(conference?: ConferenceRoom): boolean {
    const targetConference = conference || this.getActiveConference();
    if (!targetConference) return false;

    return this.organizerMuteStates.get(targetConference.id) || false;
  }

  private async completeParticipantAudioCleanup(call: EveCall): Promise<void> {
    const callKey = this.callKey(call);

    // STEP 1: Clean up conference nodes
    const node = this.nodes.get(callKey);
    if (node) {
      try {
        node.source.disconnect();
      } catch {}
      try {
        node.monitorGain.disconnect();
      } catch {}
      try {
        node.sendGain.disconnect();
      } catch {}
      this.nodes.delete(callKey);
    }

    // STEP 2: Make sure media tracks are enabled (don’t force call.isMuted flag)
    const sender = this.getAudioSender(call);
    if (sender?.track) sender.track.enabled = true;
    const receiver = this.getAudioReceiver(call);
    if (receiver?.track) receiver.track.enabled = true;

    // STEP 2b: If conference held this leg, unhold first for clean renegotiation
    try {
      if (call.isOnHold) {
        await call.unholdCall();
        await this.delay(60);
      }
    } catch (e) {
      console.warn('[conf] unhold after removal warning:', e);
    }

    // STEP 3: Restore mic as sender from SipService (or fallback)
    try {
      const sdh = (call.session as any)?.sessionDescriptionHandler as
        | SessionDescriptionHandler
        | undefined;
      const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
      if (pc) {
        const rtpSender = pc
          .getSenders()
          .find((s) => s.track?.kind === 'audio');
        if (rtpSender) {
          const newMicTrack = await this.getMicTrackFromSip(); // <<< uses sipService.activeStream / selectedMicrophone
          const oldTrack = rtpSender.track;
          await rtpSender.replaceTrack(newMicTrack);

          // If oldTrack was your internal conference-mix track, stop it to avoid leaking
          // (Only stop tracks you created; if unsure, omit the stop)
          // if (oldTrack && oldTrack.label?.includes('conf-mix')) { try { oldTrack.stop(); } catch {} }
        }
        // Ensure duplex
        pc.getTransceivers?.().forEach((t) => {
          t.direction = 'sendrecv';
        });
      }
    } catch (e) {
      console.warn('[conf] replace sender to mic failed:', e);
    }

    // STEP 4: Un-hard-mute DOM
    this.hardMuteDomSinksFor(call, false);

    // STEP 5 (optional, but robust): ensure remote element exists, bind sinkId from SipService, and play
    try {
      const remote = this.getCurrentRxMediaStream(call);
      if (remote) {
        const id = `call-audio-${call.id}`;
        let el = document.getElementById(id) as HTMLAudioElement | null;
        if (!el) {
          el = document.createElement('audio');
          el.id = id;
          el.autoplay = true;
          el.muted = false;
          el.style.display = 'none';
          document.body.appendChild(el);
        }
        el.srcObject = remote;

        const sinkId = this.getSpeakerSinkIdFromSip(); // <<< uses sipService.selectedSpeakerId
        if (sinkId && 'setSinkId' in HTMLMediaElement.prototype) {
          await (el as any).setSinkId(sinkId).catch(() => {});
        }
        await el.play().catch(() => {});
      }
    } catch (e) {
      console.warn('[conf] play 1:1 remote failed:', e);
    }

    console.log(`🧹 Audio cleanup completed for ${call.remoteUser}`);
  }

  private getAudioReceiver(call: EveCall): RTCRtpReceiver | null {
    const pc = (call.session as any)?.sessionDescriptionHandler
      ?.peerConnection as RTCPeerConnection | undefined;
    if (!pc) return null;

    return (
      pc.getReceivers().find((receiver) => receiver.track?.kind === 'audio') ||
      null
    );
  }

  // ✅ ADD: Helper method to get audio sender
  private getAudioSender(call: EveCall): RTCRtpSender | null {
    const pc = (call.session as any)?.sessionDescriptionHandler
      ?.peerConnection as RTCPeerConnection | undefined;
    if (!pc) return null;

    return (
      pc.getSenders().find((sender) => sender.track?.kind === 'audio') || null
    );
  }

  // ✅ Handle terminated participant automatically
  private async handleTerminatedParticipant(call: EveCall): Promise<void> {
    try {
      console.log(
        `🔄 Auto-removing terminated participant: ${call.remoteUser}`
      );
      await this.removeFromConference(call);
      console.log(
        `✅ Successfully auto-removed terminated participant: ${call.remoteUser}`
      );
    } catch (error: any) {
      console.error(
        `❌ Failed to auto-remove terminated participant ${call.remoteUser}:`,
        error
      );
    }
  }

  // ✅ ENHANCED: Better convertToOneToOne method using SipService
  private async convertToOneToOne(call: EveCall): Promise<void> {
    console.log(`🔄 Converting ${call.remoteUser} to 1-to-1 mode`);

    // 1) Clean up any conference nodes first
    const callKey = this.callKey(call);
    const node = this.nodes.get(callKey);
    if (node) {
      try {
        node.source.disconnect();
        node.monitorGain.disconnect();
        node.sendGain.disconnect();
      } catch (err) {
        console.warn(`⚠️ Node cleanup warning for ${call.remoteUser}:`, err);
      }
      this.nodes.delete(callKey);
    }

    // 2) Clear conference flags FIRST
    call.isInConference = false;
    call.conferenceId = null;

    // 3) Clean up any existing dedicated audio elements first
    const audioElementId = `call-audio-${call.id}`;
    const existingAudioElement = document.getElementById(
      audioElementId
    ) as HTMLAudioElement;
    if (existingAudioElement) {
      existingAudioElement.pause();
      existingAudioElement.srcObject = null;
      existingAudioElement.remove();
    }

    // 4) Ensure call is active (not held) for proper audio setup
    if (call.isOnHold) {
      console.log(`📞 Unholding ${call.remoteUser} for 1-to-1 setup`);
      await call.unholdCall();
      await this.delay(300);
    }

    // 5) ✅ CRITICAL FIX: Create SEPARATE microphone track for this participant
    try {
      console.log(
        `🔄 Creating SEPARATE microphone track for ${call.remoteUser}`
      );

      const sdh = (call.session as any)?.sessionDescriptionHandler as
        | SessionDescriptionHandler
        | undefined;
      const pc: RTCPeerConnection | undefined = sdh?.peerConnection;

      if (pc) {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender) {
          let micTrack: MediaStreamTrack | null = null;

          // ✅ Option 1: Clone from original stream if available
          if (
            this.originalMicrophoneStream &&
            this.originalMicrophoneStream.active
          ) {
            const originalTrack =
              this.originalMicrophoneStream.getAudioTracks()[0];
            if (originalTrack && originalTrack.readyState === 'live') {
              console.log(
                `🎤 Cloning original microphone track for ${call.remoteUser}`
              );
              micTrack = originalTrack.clone();
            }
          }

          // ✅ Option 2: Create new microphone stream for this participant
          if (!micTrack) {
            console.log(
              `🎤 Creating new microphone stream for ${call.remoteUser}`
            );
            const selectedMic = this.sipService.getSelectedMicrophone();
            const constraints: MediaStreamConstraints = {
              audio: selectedMic
                ? {
                    deviceId: { exact: selectedMic },
                    echoCancellation: true,
                    noiseSuppression: true,
                  }
                : { echoCancellation: true, noiseSuppression: true },
              video: false,
            };

            const newMicStream = await navigator.mediaDevices.getUserMedia(
              constraints
            );
            micTrack = newMicStream.getAudioTracks()[0];
          }

          if (micTrack) {
            await sender.replaceTrack(micTrack);
            console.log(
              `✅ Separate microphone track assigned to ${call.remoteUser}`
            );
          }
        }

        // Ensure proper transceiver directions
        pc.getTransceivers().forEach((transceiver) => {
          if (
            transceiver.receiver?.track?.kind === 'audio' ||
            transceiver.sender?.track?.kind === 'audio'
          ) {
            transceiver.direction = 'sendrecv';
          }
        });

        // Enable all audio tracks
        pc.getSenders().forEach((sender) => {
          if (sender.track?.kind === 'audio') {
            sender.track.enabled = true;
          }
        });

        pc.getReceivers().forEach((receiver) => {
          if (receiver.track?.kind === 'audio') {
            receiver.track.enabled = true;
          }
        });
      }

      console.log(`✅ Audio restoration completed for ${call.remoteUser}`);
    } catch (error) {
      console.error(
        `❌ Audio restoration failed for ${call.remoteUser}:`,
        error
      );
    }

    // 6) Wait for media to stabilize
    await this.delay(200);

    // 7) ✅ CRITICAL: Set up BOTH organizer hearing AND participant hearing
    try {
      const remoteStream = this.getCurrentRxMediaStream(call);
      if (remoteStream && remoteStream.getAudioTracks().length > 0) {
        console.log(`🔊 Setting up 1-to-1 audio for ${call.remoteUser}`);

        // ✅ FOR PARTICIPANT: Create dedicated audio element
        const audioElement = document.createElement('audio');
        audioElement.id = audioElementId;
        audioElement.autoplay = true;
        audioElement.muted = false;
        audioElement.volume = 1.0;
        audioElement.style.display = 'none';
        document.body.appendChild(audioElement);

        audioElement.srcObject = remoteStream;

        // Apply selected speaker
        const selectedSpeaker = this.sipService.getSelectedSpeaker();
        if (selectedSpeaker && 'setSinkId' in HTMLMediaElement.prototype) {
          await (audioElement as any).setSinkId(selectedSpeaker);
        }

        await audioElement.play();
        console.log(`✅ Participant audio element playing`);

        // ✅ FOR ORGANIZER: Set up the output element to hear this participant
        if (this.outputEl) {
          console.log(`🔊 Setting up organizer to hear ${call.remoteUser}`);

          // Connect the participant's audio to organizer's output
          if (this.ctx.state === 'suspended') await this.ctx.resume();

          const source = this.ctx.createMediaStreamSource(remoteStream);
          const gain = this.ctx.createGain();
          gain.gain.value = 1.0;

          source.connect(gain);
          gain.connect(this.masterGain); // This connects to the organizer's output

          console.log(`✅ Organizer can now hear ${call.remoteUser}`);
        }

        console.log(`✅ 1-to-1 audio setup complete for ${call.remoteUser}`);
      } else {
        console.warn(`⚠️ No valid remote stream for ${call.remoteUser}`);
      }
    } catch (error) {
      console.warn(
        `⚠️ Failed to setup 1-to-1 audio for ${call.remoteUser}:`,
        error
      );
    }

    // 8) Clear any conference-related muting
    this.hardMuteDomSinksFor(call, false);
    this.setRemoteTracksEnabled(call, true);

    console.log(`✅ Successfully converted ${call.remoteUser} to 1-to-1 mode`);
  }

  // ✅ FIXED: Better getMicTrackFromSip method
  private async getMicTrackFromSip(): Promise<MediaStreamTrack | null> {
    try {
      // ✅ Use SipService's getActiveStream method
      const activeStream = this.sipService.getActiveStream();
      if (
        activeStream &&
        activeStream.getAudioTracks().length > 0 &&
        activeStream.active
      ) {
        console.log('🎤 Using existing active stream from SipService');
        return activeStream.getAudioTracks()[0];
      }

      // ✅ Use SipService's getMicrophoneStream method
      console.log('🎤 Getting microphone stream from SipService');
      const micStream = await this.sipService.getMicrophoneStream();
      if (micStream && micStream.getAudioTracks().length > 0) {
        return micStream.getAudioTracks()[0];
      }

      console.warn('⚠️ No microphone track available from SipService');
      return null;
    } catch (error) {
      console.error('Failed to get microphone track from SipService:', error);
      return null;
    }
  }

  /** Speaker sinkId selected in SipService, or undefined. */
  private getSpeakerSinkIdFromSip(): string | undefined {
    const svc: any = this.sipService as any;
    return svc?.selectedSpeakerId || undefined;
  }

  // ✅ NEW: Restore organizer speaker with proper connections
  private async restoreOrganizerSpeaker(): Promise<void> {
    let el = document.getElementById(
      'organizer-conference-audio'
    ) as HTMLAudioElement | null;

    if (!el) {
      el = document.createElement('audio');
      el.id = 'organizer-conference-audio';
      el.autoplay = true;
      el.muted = false;
      el.volume = 1.0;
      el.style.display = 'none';
      document.body.appendChild(el);
    }

    // ✅ CRITICAL: Ensure srcObject is properly connected to current monitor destination
    el.srcObject = this.monitorDest.stream;

    // Apply selected speaker device
    const selectedSpeaker = this.sipService.getSelectedSpeaker?.();
    if (selectedSpeaker && 'setSinkId' in HTMLMediaElement.prototype) {
      try {
        await (el as any).setSinkId(selectedSpeaker);
      } catch (err) {
        console.warn('⚠️ setSinkId failed:', err);
      }
    }

    try {
      await el.play();
      console.log('✅ Organizer speaker restored and playing');
    } catch (err) {
      console.warn('⚠️ Could not play organizer audio:', err);
    }
  }
}
